/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ActionEvent,
    PackagePreviewModel,
    TaskPreviewModel,
} from "../../tasks/types";
import {
    ActionConstraint,
    ActionDefinition,
    PayloadElement,
    PayloadStructure,
} from "../../administration/types/Actions";
import { Utils } from "./Utils";
import { ProjectForCurrentUser } from "../services/types";
import { MetadataDefinition } from "../../administration/types/Metadata";

export default class ActionHelper {
    static canExecute(
        action: ActionDefinition,
        task: TaskPreviewModel,
        projectAssignment: ProjectForCurrentUser,
        metadataDefinitions: MetadataDefinition[],
        pkg?: PackagePreviewModel,
        events?: ActionEvent[],
    ): { canExecute: boolean; reason?: string } {
        // Check action permissions:
        if (action.permissions) {
            // Can't trigger action if user is not assigned to project
            if (!projectAssignment) {
                return { canExecute: false, reason: "No project assignment" };
            }

            const requiresAdmin = action.permissions.admin ?? false;
            const requiresRoles = (action.permissions.roles ?? []).length > 0;
            const isAdmin = projectAssignment.isAdmin ?? false;
            const hasUserRolesDefined =
                (projectAssignment.userRoles ?? []).length > 0;
            const hasRequiredRole =
                action.permissions.roles?.some((r) =>
                    projectAssignment.userRoles?.includes(r),
                ) ?? false;

            // User has to be project admin (no extra roles defined)
            if (requiresAdmin && !requiresRoles && !isAdmin) {
                return {
                    canExecute: false,
                    reason: "User is not project admin",
                };
            }

            if (requiresRoles) {
                if (!hasUserRolesDefined && !isAdmin) {
                    return {
                        canExecute: false,
                        reason: "User has no roles assigned",
                    };
                }

                if (!hasRequiredRole && !isAdmin) {
                    return {
                        canExecute: false,
                        reason: "User doesn't have required role",
                    };
                }
            }
        }

        // Meets constraints
        if (
            action.constraints &&
            action.constraints.some(
                (c) => !this.checkConstraint(c, task, metadataDefinitions, pkg),
            )
        ) {
            return { canExecute: false, reason: "Constraints not met" };
        }

        // No running actions of the same type:
        if (this.hasActionInProgress(action, task, pkg, events)) {
            return {
                canExecute: false,
                reason: "Action of the same type is in progress",
            };
        }

        return { canExecute: true, reason: undefined };
    }

    static hasActionInProgress(
        action: ActionDefinition,
        task: TaskPreviewModel,
        pkg?: PackagePreviewModel,
        events?: ActionEvent[],
    ) {
        return events
            ? events.some(
                  (e) =>
                      e.taskId === task.id &&
                      (pkg === undefined || e.packageId === pkg.id) &&
                      e.actionType === action.actionType &&
                      e.status === "Started",
              )
            : false;
    }

    static checkConstraint(
        constraint: ActionConstraint,
        task: TaskPreviewModel,
        metadataDefinitions: MetadataDefinition[],
        pkg?: PackagePreviewModel,
    ) {
        let result: boolean = true;

        let sourceValue;

        switch (constraint.fieldType) {
            case "TaskProperty":
                sourceValue =
                    task &&
                    Object.keys(task).includes(
                        Utils.unCapitalizeWord(constraint.name),
                    )
                        ? task[Utils.unCapitalizeWord(constraint.name)]
                        : undefined;
                break;
            case "AttachmentProperty":
                sourceValue =
                    pkg &&
                    Object.keys(pkg).includes(
                        Utils.unCapitalizeWord(constraint.name),
                    )
                        ? pkg[Utils.unCapitalizeWord(constraint.name)]
                        : undefined;
                break;
            case "Metadata":
                sourceValue = Utils.getMetadataValue(
                    metadataDefinitions.find(
                        (md) => md.name === constraint.name,
                    )!,
                    task.metadata,
                    metadataDefinitions,
                );
                break;
            default:
                sourceValue = undefined;
                break;
        }

        switch (constraint.operator) {
            case "Equal":
                result = sourceValue == constraint.value;
                break;
            case "NotEqual":
                result = sourceValue != constraint.value;
                break;
            case "IsNull":
                result = sourceValue === undefined || sourceValue === null;
                break;
            case "NotNull":
                result = sourceValue !== undefined && sourceValue !== null;
                break;
            case "AnyOf":
                result = sourceValue == constraint.value;
                break;
            case "NoneOf":
                result = sourceValue == constraint.value;
                break;
            case "GreaterThan":
                result =
                    constraint.value !== undefined &&
                    sourceValue > constraint.value;
                break;
            case "LessThan":
                result =
                    constraint.value !== undefined &&
                    sourceValue < constraint.value;
                break;
            default:
                result = true;
                break;
        }

        // console.log('Constraint check [', constraint.fieldType, ', ', constraint.name, ', ',
        //     constraint.operator, ', ', constraint.value, '] => [', sourceValue, ', ', result, ']');

        return constraint.kind === "Requirement" ? result : !result;
    }

    static generateSampleJSON(
        structure: PayloadStructure,
    ): Record<string, any> {
        const generateElement = (element: PayloadElement): any => {
            switch (element.type) {
                case "Property":
                    return "string"; // Default value for properties
                case "Array":
                    return element.children
                        ? element.children.map((child) =>
                              generateElement(child),
                          )
                        : [];
                case "Object":
                    return element.children
                        ? element.children.reduce(
                              (obj, child) => {
                                  obj[child.name || "unnamed"] =
                                      generateElement(child);
                                  return obj;
                              },
                              {} as Record<string, any>,
                          )
                        : {};
                default:
                    return null;
            }
        };

        return structure.elements.reduce(
            (obj, element) => {
                obj[element.name || "unnamed"] = generateElement(element);
                return obj;
            },
            {} as Record<string, any>,
        );
    }
}
